/**
 * ProgramDetail — single program view showing the workouts inside.
 *
 * Athlete tapped a program from ProgramsList; this page surfaces the full
 * week/day schedule + hydrated workout list so they can jump straight into
 * the runner. Sachin spec 2026-02-27.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowLeft, ArrowRight, Lock, Calendar, Clock, Layers } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFICULTY_TONE = {
    beginner: "text-emerald-300",
    intermediate: "text-sky-300",
    advanced: "text-amber-300",
};

export default function ProgramDetail() {
    const { programId } = useParams();
    const { user, loading, activeAthleteId } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const athleteId = searchParams.get("athlete_id") || activeAthleteId || "";
    const [program, setProgram] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!loading && !user) navigate("/", { replace: true });
    }, [loading, user, navigate]);

    useEffect(() => {
        if (!user || !programId) return;
        axios.get(`${API}/programs/${programId}`, {
            params: athleteId ? { athlete_id: athleteId } : {},
            withCredentials: true,
        })
            .then((r) => setProgram(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load program"));
    }, [user, programId, athleteId]);

    if (loading || !user) return null;
    if (err) {
        return (
            <div className="min-h-screen bg-brand-obsidian text-white pt-24 px-6 max-w-2xl mx-auto">
                <Link to="/dashboard/workouts" className="text-xs text-brand-red">← Programs</Link>
                <div className="mt-6 border border-amber-500/40 bg-amber-950/20 p-5 text-amber-200">{err}</div>
            </div>
        );
    }
    if (!program) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }
    const woMap = Object.fromEntries((program.workouts || []).map((w) => [w.workout_id, w]));
    const backHref = program.category
        ? `/dashboard/programs?category=${program.category}${athleteId ? `&athlete_id=${encodeURIComponent(athleteId)}` : ""}`
        : "/dashboard/workouts";

    return (
        <div data-testid="program-detail" className="min-h-screen bg-brand-obsidian text-white pt-20 pb-24 md:pb-12 px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
                <Link to={backHref} className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-brand-red mb-3">
                    <ArrowLeft className="w-3 h-3" /> Back to programs
                </Link>
                <div className="flex items-start gap-2 flex-wrap mb-1">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red">
                        {program.category || "Program"}
                        {program.body_focus && (
                            <span className="text-sky-300 ml-2">· {program.body_focus.replace("_", " ")}</span>
                        )}
                    </div>
                </div>
                <h1 className="font-display text-3xl md:text-5xl tracking-wider mb-2">{program.title}</h1>
                <div className="flex items-center gap-4 text-[11px] tracking-[0.15em] uppercase text-zinc-400 flex-wrap mb-3">
                    {program.difficulty && (
                        <span className={DIFFICULTY_TONE[program.difficulty] || "text-zinc-300"}>
                            {program.difficulty}
                        </span>
                    )}
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {program.duration_weeks || 4} weeks</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {program.days_per_week || 5}×/wk</span>
                    <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" /> {(program.workouts || []).length} workouts</span>
                </div>
                {program.description && (
                    <p className="text-sm text-zinc-300 mb-5 leading-relaxed">{program.description}</p>
                )}
                {program.locked && (
                    <div
                        data-testid="program-locked-banner"
                        className="border border-amber-500/40 bg-amber-950/20 p-4 mb-5 text-sm text-amber-200 flex items-start gap-2"
                    >
                        <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                        <div>
                            <div className="font-semibold tracking-wide">This program requires an active subscription</div>
                            <div className="text-xs text-zinc-300 mt-1">
                                <Link to="/dashboard/billing" className="underline">Manage subscription</Link> · then refresh.
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-4">
                    {(program.weeks || []).map((w) => (
                        <div key={w.week_number} className="border border-zinc-800 rounded-2xl bg-zinc-950/40">
                            <div className="px-4 py-3 border-b border-zinc-800 text-[10px] tracking-[0.3em] uppercase text-zinc-300">
                                Week {w.week_number}{w.week_name ? ` · ${w.week_name}` : ""}
                            </div>
                            <div className="divide-y divide-zinc-900">
                                {(w.days || []).map((d) => {
                                    const dayWorkoutIds = (d.workout_ids && d.workout_ids.length)
                                        ? d.workout_ids
                                        : (d.workout_id ? [d.workout_id] : []);
                                    return (
                                        <div key={d.day_number} className="px-4 py-3">
                                            <div className="flex items-baseline gap-2 mb-1">
                                                <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                                                    Day {d.day_number}{d.day_name ? ` · ${d.day_name}` : ""}
                                                </span>
                                                {d.is_rest_day && (
                                                    <span className="text-[10px] tracking-[0.2em] uppercase text-emerald-300">Rest</span>
                                                )}
                                            </div>
                                            {!d.is_rest_day && dayWorkoutIds.length === 0 && (
                                                <div className="text-[11px] text-zinc-600 italic">Empty</div>
                                            )}
                                            <div className="space-y-1.5">
                                                {(() => {
                                                    const wos = dayWorkoutIds.map((wid) => woMap[wid]).filter(Boolean);
                                                    const missing = dayWorkoutIds.filter((wid) => !woMap[wid]);
                                                    // 2+ available workouts on one day → show them as ONE
                                                    // combined workout (names joined) that runs back-to-back.
                                                    if (wos.length >= 2) {
                                                        const ids = wos.map((w) => w.workout_id);
                                                        const title = wos.map((w) => w.title).join(" + ");
                                                        const totalDur = wos.reduce((s, w) => s + (w.estimated_duration || 0), 0);
                                                        const runHref = `/dashboard/workouts/combined/run?ids=${ids.join(",")}${athleteId ? `&athlete_id=${encodeURIComponent(athleteId)}` : ""}`;
                                                        return (
                                                            <>
                                                                <Link
                                                                    to={program.locked ? "/dashboard/billing" : runHref}
                                                                    data-testid={`program-day-combined-${d.day_number}`}
                                                                    className={`flex items-center justify-between gap-2 border border-zinc-800 px-3 py-2 transition-colors ${program.locked ? "opacity-60 hover:border-amber-700" : "hover:border-brand-red"}`}
                                                                >
                                                                    <div className="min-w-0">
                                                                        <div className="text-sm text-white truncate">{title}</div>
                                                                        <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500">
                                                                            {wos.length} workouts combined{totalDur ? ` · ${totalDur} min` : ""}
                                                                        </div>
                                                                    </div>
                                                                    <ArrowRight className="w-4 h-4 text-zinc-500 shrink-0" />
                                                                </Link>
                                                                {missing.map((wid) => (
                                                                    <div key={wid} className="text-[11px] text-zinc-600 italic">· Workout unavailable</div>
                                                                ))}
                                                            </>
                                                        );
                                                    }
                                                    return dayWorkoutIds.map((wid) => {
                                                        const wo = woMap[wid];
                                                        if (!wo) {
                                                            return (
                                                                <div key={wid} className="text-[11px] text-zinc-600 italic">
                                                                    · Workout unavailable
                                                                </div>
                                                            );
                                                        }
                                                        const runHref = `/dashboard/workouts/${wid}/run${athleteId ? `?athlete_id=${encodeURIComponent(athleteId)}` : ""}`;
                                                        return (
                                                            <Link
                                                                key={wid}
                                                                to={program.locked ? "/dashboard/billing" : runHref}
                                                                data-testid={`program-day-workout-${wid}`}
                                                                className={`flex items-center justify-between gap-2 border border-zinc-800 px-3 py-2 transition-colors ${
                                                                    program.locked
                                                                        ? "opacity-60 hover:border-amber-700"
                                                                        : "hover:border-brand-red"
                                                                }`}
                                                            >
                                                                <div className="min-w-0">
                                                                    <div className="text-sm text-white truncate">{wo.title}</div>
                                                                    <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500">
                                                                        {wo.category}{wo.body_focus ? ` · ${wo.body_focus.replace("_"," ")}` : ""}{wo.estimated_duration ? ` · ${wo.estimated_duration} min` : ""}
                                                                    </div>
                                                                </div>
                                                                <ArrowRight className="w-4 h-4 text-zinc-500 shrink-0" />
                                                            </Link>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                            {d.notes && (
                                                <div className="text-[10px] text-zinc-500 mt-1.5">Note: {d.notes}</div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
